import { checkDocumentSize, MAX_DIMENSION } from '../limits';

/**
 * The decision made when a user opens an image: use it as-is, offer to
 * downscale it, or refuse. Kept separate from decoding so the policy is unit
 * tested and the browser code stays dumb.
 */

export type OpenPlan =
  | { action: 'use'; width: number; height: number }
  | {
      action: 'downscale';
      originalWidth: number;
      originalHeight: number;
      width: number;
      height: number;
      scale: number;
    }
  | { action: 'reject'; reason: string };

export function planOpenedImage(
  width: number,
  height: number,
  maxDimension = MAX_DIMENSION,
): OpenPlan {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    return { action: 'reject', reason: 'That image has no usable dimensions.' };
  }
  const check = checkDocumentSize(width, height, maxDimension);
  if (check.ok) {
    return { action: 'use', width: Math.round(width), height: Math.round(height) };
  }
  const scale = check.suggestedScale ?? 1;
  const scaledWidth = Math.max(1, Math.floor(width * scale));
  const scaledHeight = Math.max(1, Math.floor(height * scale));
  return {
    action: 'downscale',
    originalWidth: Math.round(width),
    originalHeight: Math.round(height),
    width: scaledWidth,
    height: scaledHeight,
    scale,
  };
}

export function describePlan(plan: OpenPlan): string {
  switch (plan.action) {
    case 'use':
      return `${plan.width} × ${plan.height}`;
    case 'downscale':
      return `${plan.originalWidth} × ${plan.originalHeight} is over the limit; it will be resized to ${plan.width} × ${plan.height}.`;
    case 'reject':
      return plan.reason;
  }
}
